'use client'

import { Route } from 'next'
import { useTranslations } from 'next-intl'
import { useRouter, useSearchParams } from 'next/navigation'

import { ApiError } from '@/libs/api'
import createAuthAPI from '@/services/auth-service'
import Form, { TField } from '@/shared-gui-components/form'
import { tryCatch } from '@/shared-gui-components/utils'
import { TLoginRequest } from '@/shared-types/generated/TLoginRequest'
import { Nullable } from '@/shared-types/types'

import AuthCard from './auth-card'
import submitButton from './submit-button'

const safeNext = (next: Nullable<string>): Route => (next && next.startsWith('/') && !next.startsWith('//') ? (next as Route) : '/')

const LoginForm = () => {
    const t = useTranslations('login')
    const router = useRouter()
    const searchParams = useSearchParams()

    const fields: TField<TLoginRequest>[] = [
        {
            label: t('login'),
            name: 'login',
            type: 'text',
            props: { autoFocus: true, autoComplete: 'username' },
            validation: { required: true },
        },
        {
            label: t('password'),
            name: 'password',
            type: 'password',
            props: { autoComplete: 'current-password' },
            validation: { required: true },
        },
    ]

    // An error thrown here is shown by the form as a toast.
    const onSubmit = async (data: TLoginRequest) => {
        const [, err] = await tryCatch(createAuthAPI().login(data))
        if (err)
            throw new Error(err instanceof ApiError && err.status == 400 ? t('invalid') : t('error'))

        router.push(safeNext(searchParams.get('next')))
        router.refresh()
    }

    return (
        <AuthCard title={t('title')} subtitle={t('subtitle')}>
            <Form fields={fields} onSubmit={onSubmit} footer={submitButton(t('submit'))} resetOnSubmit={false} />
        </AuthCard>
    )
}

export default LoginForm
